/**
 * Schedule week view shapes — crew × day grid with per-day load.
 *
 * The week endpoint returns one row per crew member, each containing
 * a per-day breakdown of assigned hours and task count. The frontend
 * renders this as a load heatmap grid with click-through to the
 * dispatch board day view.
 */
import { z } from 'zod';
import { UuidSchema } from './common.js';
import { CrewMemberWithUserSchema } from './users.js';
import { TaskTypeSchema, TaskStatusSchema } from './enums.js';
/** Summary of one task, enough to render a compact list in a cell tooltip. */
export const ScheduleTaskSummarySchema = z.object({
    id: UuidSchema,
    project_id: UuidSchema,
    title: z.string(),
    project_title: z.string(),
    type: TaskTypeSchema,
    status: TaskStatusSchema,
    scheduled_start: z.string().nullable(),
    scheduled_end: z.string().nullable(),
});
/** One day's load for a single crew member. */
export const ScheduleDayCellSchema = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    assigned_hours: z.number().min(0),
    task_count: z.number().int().min(0),
    tasks: z.array(ScheduleTaskSummarySchema),
});
/** One crew member's week row. */
export const ScheduleCrewRowSchema = z.object({
    crew_member: CrewMemberWithUserSchema,
    capacity_hours_per_day: z.number().min(0),
    days: z.array(ScheduleDayCellSchema),
});
/** Aggregate totals for the header. */
export const ScheduleWeekSummarySchema = z.object({
    total_available_hours: z.number().min(0),
    total_assigned_hours: z.number().min(0),
    total_task_count: z.number().int().min(0),
    unassigned_count: z.number().int().min(0),
});
export const ScheduleWeekResponseSchema = z.object({
    start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    end_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    days: z.number().int().positive(),
    summary: ScheduleWeekSummarySchema,
    crew_rows: z.array(ScheduleCrewRowSchema),
});
export const ScheduleWeekQuerySchema = z.object({
    start: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD')
        .describe('Monday of the target week'),
    days: z
        .coerce.number()
        .int()
        .min(5)
        .max(14)
        .default(7)
        .describe('Number of days to include (5 = work week, 7 = full, 14 = 2-week)'),
});
//# sourceMappingURL=schedule.js.map